/**
 * Needs your attention: holds and when they expire (Extend / Release), needs a new day (Find a day opens
 * the Book sheet for them), unconfirmed for tomorrow and proposals awaiting an answer (Record the
 * answer), visits past their day still open (their actions), requests still with the scheduler,
 * processor rows awaiting review, and "slots run out on <date>" inside 45 days. Each item links to the
 * action that resolves it. For the administrator and co-administrators, the home visits that need a follow-up
 * (1.5.12). 19 Oct 2026 (D-1019-ATTN): no Companion & Wellness block here — the Housekeeping screens show Housekeeping
 * and Tech Support only (PR #29); the Companion items live on the house Today, the Companion tab and ProgramHome.
 */
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { AttentionItem, AttentionKind } from '@shared/api-types';
import type { Role } from '@shared/roles';
import { formatClockOf, formatDayShort, formatDayMedium } from '@/lib/dates';
import { useAttention } from '@/lib/queries';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Updated } from '@/components/Updated';
import { CallButton, CallLink } from '@/components/Contact';
import { useBooking } from '@/features/booking/BookingProvider';
import type { ActionKind } from '@/features/booking/ActionSheet';
import { useFinish } from '@/features/finish/FinishProvider';
import { useCompose } from '@/features/messages/ComposeProvider';
import { markSeen, queueReminders } from '@/lib/messages';
import { useAnnounce } from '@/components/Announcer';
import { useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { addDaysToKey, serviceDateKey } from '@shared/dates';
import { formatInstant } from '@/lib/dates';
import { ApprovalItem } from './ApprovalItem';
import { HomeVisitFollowUpItem } from './HomeVisitFollowUpItem';
import { TechNoAgreementItem } from './TechNoAgreementItem';
import { ReviewVisitLink } from '@/features/review/ReviewVisitLink';
import { openWords } from '@shared/press';
import { Icon } from '@/components/Icons';
import { OutcomeGloss } from '@/components/OutcomeGloss';
import { postNotBookedDone, useRefreshExtra } from '@/features/extra/api';
import { AUTH_EXHAUSTED, BOOKING_OPERATIONS, canExtraVisit } from '@shared/extra-support';

const ORDER: AttentionKind[] = ['pendingCloseout', 'notBooked', 'approval', 'tenantAnswer', 'awaitingAgreement', 'techFollowUp', 'homeVisitFollowUp', 'techNoAgreement', 'reply', 'completionReturned', 'hold', 'notHome', 'needsNewDay', 'unconfirmedTomorrow', 'proposal', 'pastDay', 'queued', 'completionReview', 'awaitingReview', 'slotCliff'];
const DOT: Record<AttentionKind, string> = {
  hold: 'var(--st-hold-dot)',
  needsNewDay: 'var(--st-attn-dot)',
  notHome: 'var(--st-attn-dot)',
  unconfirmedTomorrow: 'var(--st-sched-dot)',
  proposal: 'var(--brand)',
  pastDay: 'var(--st-attn-dot)',
  awaitingReview: 'var(--st-attn-dot)',
  slotCliff: 'var(--st-hold-dot)',
  queued: 'var(--st-sched-dot)',
  completionReturned: 'var(--st-attn-dot)',
  completionReview: 'var(--st-attn-dot)',
  tenantAnswer: 'var(--st-conf-dot)',
  reply: 'var(--st-attn-dot)',
  approval: 'var(--st-hold-dot)',
  techFollowUp: 'var(--st-attn-dot)',
  homeVisitFollowUp: 'var(--st-attn-dot)',
  techNoAgreement: 'var(--st-attn-dot)',
  pendingCloseout: 'var(--st-attn-dot)',
  notBooked: 'var(--st-attn-dot)',
  awaitingAgreement: 'var(--st-hold-dot)',
};
const SHOW = 3;

/** 19 Oct 2026 (D-1019-ATTN): kinds that are Tech Support's alone. */
const TECH_KINDS: ReadonlySet<AttentionKind> = new Set(['techFollowUp', 'awaitingAgreement', 'techNoAgreement']);
/** A Tech Support item: its visit is a Tech ticket, it is the tech's close-out, or its kind is Tech Support's. */
export const isTechItem = (i: AttentionItem): boolean =>
  TECH_KINDS.has(i.kind) || (i.appointment?.program ?? '').toUpperCase() === 'TECH' || (i.kind === 'pendingCloseout' && i.text === 'Tech Support');

/**
 * 1.5.22 §B: `listedToClose` — the visits Today's "visits to close out" card already lists (with "late · n min"
 * on the overdue ones). Their `pendingCloseout` items are not said a second time here, and not counted. A pending
 * close-out that card cannot show — a Tech Support ticket, a volunteer's visit, a visit of an earlier day — stays.
 */
/**
 * 18 Oct 2026 (D-1018-HK): the Day's one attention list. `closeouts` — the day's visits to close out, which the Day
 * computes (they come first, before every other group); `top` — the card stands directly under the page heading, and
 * ends with "Nothing else needs your attention" when there is nothing.
 */
export interface CloseoutGroup {
  count: number;
  late: number;
  /** The `<li>` lines, one per visit (the Day renders them: Finish / Start / End with their own test ids). */
  rows: ReactNode;
}

export function AttentionCard({ role, staff, listedToClose, openLong = [], closeouts, top, tech = 'hide' }: { role: Role; staff: boolean; /** 19 Oct 2026 (D-1019-ATTN): Tech Support's items — left out (the office roles have /tech), or listed apart under their own heading */ tech?: 'hide' | 'only'; listedToClose?: ReadonlySet<string>; /** 1.6.5: visits open past their planned length — "still there?" */ openLong?: readonly { appointmentId: string; unit: string; minutes: number }[]; closeouts?: CloseoutGroup | null; top?: boolean }) {
  const { t, i18n } = useTranslation();
  const q = useAttention();
  const booking = useBooking();
  const finish = useFinish();
  const compose = useCompose();
  const announce = useAnnounce();
  const qc = useQueryClient();
  const [busy, setBusy] = useState<string | null>(null);
  const refreshExtra = useRefreshExtra();
  /** 20 Oct 2026 (D-1020-EXTRA): Done on a refused booking — it leaves the list; nothing else changes. */
  const refusedDone = async (i: AttentionItem) => {
    setBusy(i.id);
    try {
      await postNotBookedDone(i.id);
      announce(t('extra1020.doneAnnounce', { who: who(i) }));
      refreshExtra();
    } catch {
      announce(t('extra1020.doneFailed'), 'assertive');
    } finally {
      setBusy(null);
    }
  };

  const seen = async (i: AttentionItem) => {
    if (!i.communicationId) return;
    setBusy(i.id);
    try {
      await markSeen(i.communicationId);
      announce(t('attention.seenDone', { who: who(i) }));
      void qc.invalidateQueries({ queryKey: ['attention'] });
    } finally {
      setBusy(null);
    }
  };
  const remind = async () => {
    setBusy('remind');
    try {
      const r = await queueReminders(addDaysToKey(serviceDateKey(), 1));
      const msg = r.sendAt ? t('attention.remindersQueued', { count: r.queued, time: formatInstant(r.sendAt, i18n.language), calls: r.skipped.length }) : t('attention.remindersOff');
      announce(msg);
      void qc.invalidateQueries({ queryKey: ['messages'] });
    } catch {
      announce(t('attention.remindersFailed'), 'assertive');
    } finally {
      setBusy(null);
    }
  };

  // The review group is the administrator's (the review queue is inside the sensitive boundary).
  const saidAbove = (i: AttentionItem) => i.kind === 'pendingCloseout' && !!listedToClose?.has(i.appointment?.appointmentId ?? i.id.replace(/^CLOSEOUT-/, ''));
  const items = (q.data?.items ?? []).filter((i) => i.kind !== 'reply' && (i.kind !== 'completionReview' || role === 'admin') && (i.kind !== 'techNoAgreement' || staff) && !saidAbove(i)); // 1.6.8: the agreements are the two office roles'
  const groupsOf = (list: AttentionItem[]) => ORDER.map((kind) => ({ kind, list: list.filter((i) => i.kind === kind) })).filter((g) => g.list.length);
  // 'only': the Tech Support page's own list (the office roles) — its items alone, nothing of Housekeeping's
  const groups = tech === 'only' ? groupsOf(items.filter(isTechItem)) : groupsOf(items.filter((i) => !isTechItem(i)));
  const closeoutCount = closeouts?.count ?? 0;
  const countOf = (gs: typeof groups) => gs.reduce((n, g) => n + (g.kind === 'slotCliff' || g.kind === 'queued' ? 1 : g.list.length), 0);
  const total = countOf(groups) + openLong.length + closeoutCount;

  const who = (i: AttentionItem) => [i.unit, i.tenantName].filter(Boolean).join(' · ');

  const act = (i: AttentionItem, kind: ActionKind, label: string) =>
    booking.canBook && i.appointment ? (
      <Button
        size="sm"
        variant="secondary"
        aria-label={`${label} · ${who(i)}`}
        data-testid={`attn-act-${i.id}`}
        onClick={(e) => booking.openActions(i.appointment!, kind, e.currentTarget)}
      >
        {label}
      </Button>
    ) : null;

  /** A visit past its day can still be closed out (late, with the real date) — or marked completed when it takes no record. */
  const closeLate = (i: AttentionItem) => {
    const a = i.appointment;
    if (!finish.canFinish || !a) return null;
    const target = { appointmentId: a.appointmentId, unit: a.unit, tenantName: a.tenantName, serviceDateKey: a.serviceDateKey };
    return a.kind === 'tenant' ? (
      <Button size="sm" aria-label={`${t('today.finish')} · ${who(i)}`} data-testid={`attn-finish-${i.id}`} onClick={(e) => finish.openFinish(target, e.currentTarget)}>
        {t('today.finish')}
      </Button>
    ) : (
      <Button size="sm" variant="secondary" aria-label={t('card168.endFor', { unit: a.unit })} data-testid={`attn-mark-${i.id}`} onClick={() => finish.markCompleted(target)}>
        {t('card168.end')}
      </Button>
    );
  };

  const line = (kind: AttentionKind, list: AttentionItem[]) => {
    switch (kind) {
      case 'hold':
        return list.map((i) => (
          <li key={i.id}>
            <span className="dot" style={{ background: DOT.hold }} aria-hidden="true" />
            <div className="t">
              <b>
                {who(i)} · {t('attention.holdTitle')}
              </b>
              <span>
                {i.until
                  ? t('attention.holdUntil', { day: formatDayShort(i.dayKey ?? '', i18n.language), time: formatClockOf(i.until, i18n.language) })
                  : t('attention.holdNoExpiry')}
              </span>
            </div>
            {act(i, 'menu', t('attention.holdAct'))}
          </li>
        ));
      case 'unconfirmedTomorrow':
      case 'proposal':
      case 'pastDay':
        return [
          <li key={`${kind}-head`} className="attn-head">
            <span className="dot" style={{ background: DOT[kind] }} aria-hidden="true" />
            <div className="t">
              <b>{t(`attention.${kind}`, { count: list.length })}</b>
            </div>
            {kind === 'unconfirmedTomorrow' && compose.canCompose ? (
              <Button size="sm" variant="secondary" disabled={busy === 'remind'} onClick={() => remind()} data-testid="queue-reminder">
                {t('attention.queueReminder')}
              </Button>
            ) : null}
          </li>,
          ...list.slice(0, SHOW).map((i) => (
            <li key={i.id} className="attn-sub">
              <div className="t">
                <span>
                  {who(i)}
                  {i.dayKey ? ` · ${formatDayShort(i.dayKey, i18n.language)}` : ''}
                </span>
              </div>
              {kind === 'pastDay' ? closeLate(i) : null}
              {kind === 'pastDay' ? act(i, 'menu', t('actions.open')) : act(i, 'answer', t('attention.recordAnswer'))}
            </li>
          )),
          ...(list.length > SHOW
            ? [
                <li key={`${kind}-more`} className="attn-sub">
                  <Link to="/schedule">{t('schedule.andMore', { count: list.length - SHOW })}</Link>
                </li>,
              ]
            : []),
        ];
      case 'queued':
        return (
          <li key="queued" data-testid="attention-queued">
            <span className="dot" style={{ background: DOT.queued }} aria-hidden="true" />
            <div className="t">
              <b>{t('attention.queued', { count: list.length })}</b>
              <span>{t('attention.queuedSub')}</span>
            </div>
          </li>
        );
      // 1.5.16: the crew found nobody home — the visit still stands and the office decides.
      // 1.5.17: "Tenant not home · no permission · <time>" with Call and Move; Office action for the two office roles.
      case 'notHome':
        return list.map((i) => (
          <li key={i.id} data-testid={`attn-not-home-${i.id}`}>
            <span className="dot" style={{ background: DOT.notHome }} aria-hidden="true" />
            <div className="t">
              <b>
                {who(i)} · {i.doorAt ? t('door17.attention', { time: formatClockOf(i.doorAt, i18n.language) }) : t('door16.attention')}
              </b>
              <span>{i.text}</span>
            </div>
            <CallButton phone={i.phone ?? null} name={i.tenantName ?? i.unit ?? ''} />
            {booking.canBook && i.appointment ? (
              <Button size="sm" variant="secondary" aria-label={`${t('door17.move')} · ${who(i)}`} data-testid={`attn-move-${i.id}`} onClick={(e) => booking.openActions(i.appointment!, 'reschedule', e.currentTarget)}>
                {t('door17.move')}
              </Button>
            ) : null}
            {staff && i.appointment ? (
              <Button
                size="sm"
                variant="secondary"
                aria-label={`${t('office17.button')} · ${who(i)}`}
                data-testid={`attn-office-${i.id}`}
                onClick={(e) => booking.openActions(i.appointment!, 'menu', e.currentTarget, { office: true })}
              >
                {t('office17.button')}
              </Button>
            ) : null}
          </li>
        ));
      case 'needsNewDay':
        return list.map((i) => (
          <li key={i.id}>
            <span className="dot" style={{ background: DOT.needsNewDay }} aria-hidden="true" />
            <div className="t">
              <b>
                {who(i)} · {t('attention.needsNewDay')}
              </b>
              <span>{i.dayKey && i.appointment?.program?.toUpperCase() !== 'TECH' ? t('attention.movedOut', { day: formatDayShort(i.dayKey, i18n.language) }) : i.text}</span>
            </div>
            {/* 1.8.5 (D-1.8.5-4): a Tech Support ticket the tech found nobody home for — Find a day books housekeeping, so the
                ticket is opened instead (its new day is booked from Book → Tech Support, which closes this item) */}
            {i.appointment?.program?.toUpperCase() === 'TECH' ? (
              <Link className="btn secondary sm" to={`/tech/${encodeURIComponent(i.appointment.appointmentId)}`} data-testid={`attn-newday-ticket-${i.id}`}>
                {t('attention.openTicket')}
              </Link>
            ) : i.inProgress ? (
              <span className="status sched has-icon" data-testid="find-day-pending">
                {t('attention.bookingPending')}
              </span>
            ) : booking.canBook ? (
              <Button
                size="sm"
                variant="secondary"
                aria-label={`${t('attention.findDay')} · ${who(i)}`}
                data-testid={`find-day-${i.id}`}
                onClick={(e) =>
                  booking.openBook(
                    { followUp: { id: i.id, tenantId: i.tenantId ?? i.appointment?.tenantId ?? '', unit: i.unit ?? '', name: i.tenantName ?? '', appointment: i.appointment ?? null } },
                    e.currentTarget,
                  )
                }
              >
                {t('attention.findDay')}
              </Button>
            ) : null}
          </li>
        ));
      // 20 Oct 2026 (D-1020-EXTRA): a request the processor refused is never silent — its words verbatim, kept 14 days until Done
      case 'notBooked':
        return list.map((i) => {
          const booking_ = BOOKING_OPERATIONS.has(i.subject ?? '');
          const tenant = { tenantId: i.tenantId ?? '', unit: i.unit ?? '', name: i.tenantName ?? '' };
          const day = i.dayKey ? formatDayShort(i.dayKey, i18n.language) : '';
          return (
            <li key={i.id} data-testid={`attn-not-booked-${i.id}`}>
              <span className="dot" style={{ background: DOT.notBooked }} aria-hidden="true" />
              <div className="t">
                <b>{t(booking_ ? 'extra1020.notBooked' : 'extra1020.notDone', { who: [i.unit, day].filter(Boolean).join(' · ') })}</b>
                <span data-testid="not-booked-message">{i.text}</span>
                <OutcomeGloss code={i.action} />
              </div>
              {booking.canBook && booking_ && i.tenantId ? (
                <Button size="sm" variant="secondary" aria-label={`${t('extra1020.bookAgain')} · ${who(i)}`} data-testid={`not-booked-again-${i.id}`} onClick={(e) => booking.openBook({ tenant, dayKey: i.dayKey ?? null }, e.currentTarget)}>
                  {t('extra1020.bookAgain')}
                </Button>
              ) : null}
              {booking.canBook && booking_ && i.tenantId && i.action === AUTH_EXHAUSTED && canExtraVisit(role) ? (
                <Button size="sm" variant="secondary" aria-label={`${t('extra1020.extraVisit')} · ${who(i)}`} data-testid={`not-booked-extra-${i.id}`} onClick={(e) => booking.openBook({ tenant, dayKey: i.dayKey ?? null, extra: true }, e.currentTarget)}>
                  {t('extra1020.extraVisit')}
                </Button>
              ) : null}
              <Button size="sm" variant="ghost" disabled={busy === i.id} aria-label={`${t('extra1020.done')} · ${who(i)}`} data-testid={`not-booked-done-${i.id}`} onClick={() => refusedDone(i)}>
                {t('extra1020.done')}
              </Button>
            </li>
          );
        });
      case 'approval':
        return list.map((i) => <ApprovalItem key={i.id} item={i} isAdmin={role === 'admin'} dot={DOT.approval} />);
      case 'homeVisitFollowUp':
        return list.map((i) => <HomeVisitFollowUpItem key={i.id} item={i} dot={DOT.homeVisitFollowUp} />);
      // 1.6.7: "n tenants with no Tech Support agreement" — one line, the names each a link to the profile
      case 'techNoAgreement': // 1.6.8: only tenants with a booked Tech Support visit and no completed agreement
        return list.map((i) => <TechNoAgreementItem key={i.id} item={i} dot={DOT.techNoAgreement} />);
      case 'techFollowUp':
        return list.map((i) => (
          <li key={i.id} data-testid={`attn-tech-${i.id}`}>
            <span className="dot" style={{ background: DOT.techFollowUp }} aria-hidden="true" />
            <div className="t">
              <b>
                {who(i)} · {t('attention.techFollowUp')}
              </b>
              <span style={{ whiteSpace: 'pre-line' }}>{i.text}</span>
            </div>
            {i.appointment ? (
              <Link className="btn secondary sm" to={`/tech/${encodeURIComponent(i.appointment.appointmentId)}`}>
                {t('attention.openTicket')}
              </Link>
            ) : null}
          </li>
        ));
      case 'awaitingAgreement':
        return list.map((i) => (
          <li key={i.id} data-testid={`attn-agreement-${i.id}`}>
            <span className="dot" style={{ background: DOT.awaitingAgreement }} aria-hidden="true" />
            <div className="t">
              <b>
                {who(i)} · {t('agreement.awaiting')}
              </b>
              <span>
                {i.dayKey ? `${t('agreement.techOn', { day: formatDayShort(i.dayKey, i18n.language) })} · ` : ''}
                {/* 1.8.3: no number in words on Today — the Call beside it dials */}
                {i.text ? t('today183.agreementCall') : t('agreement.noPhone')}
              </span>
            </div>
            {i.text ? (
              <CallLink className="btn secondary sm" phone={i.text} label={`${t('agreement.call')} · ${who(i)}`} data-testid={`attn-agreement-call-${i.id}`}>
                {t('agreement.call')}
              </CallLink>
            ) : null}
            {i.tenantId ? (
              <Link className="btn secondary sm" to={`/people/${encodeURIComponent(i.tenantId)}`}>
                {t('agreement.openRecord')}
              </Link>
            ) : null}
          </li>
        ));
      case 'pendingCloseout':
        return list.map((i) => (
          <li key={i.id} data-testid={`attn-pending-${i.id}`}>
            <span className="dot" style={{ background: DOT.pendingCloseout }} aria-hidden="true" />
            <div className="t">
              <b>
                {who(i)} · {t('attention.pendingCloseout')}
              </b>
              <span>{i.text}</span>
            </div>
            {i.appointment?.program === 'TECH' ? (
              <Link className="btn secondary sm" to={`/tech/${encodeURIComponent(i.appointment.appointmentId)}`}>
                {t('attention.openTicket')}
              </Link>
            ) : i.appointment?.program === 'HOUSEKEEPING' ? closeLate(i) : null}
            {/* 1.6.5: Review visit — what happened to it so far, on one screen */}
            {i.appointment ? <ReviewVisitLink role={role} visit={i.appointment} /> : null}
          </li>
        ));
      case 'tenantAnswer':
        return list.map((i) => (
          <li key={i.id} data-testid={`attn-answer-${i.id}`}>
            <span className="dot" style={{ background: DOT.tenantAnswer }} aria-hidden="true" />
            <div className="t">
              <b>
                {who(i)} · {t(`attention.answer.${i.action ?? 'confirm'}`)}
              </b>
              <span>
                {i.text}
                {i.at ? ` · ${formatInstant(i.at, i18n.language)}` : ''}
              </span>
            </div>
            {compose.canCompose ? (
              <Button size="sm" variant="secondary" disabled={busy === i.id} aria-label={`${t('attention.seen')} · ${who(i)}`} onClick={() => seen(i)} data-testid={`attn-seen-${i.id}`}>
                {t('attention.seen')}
              </Button>
            ) : null}
          </li>
        ));
      // 23 Oct 2026 (D-1023-INBOX): a reply or a bounced email is never listed here — it waits in Messages → Inbox
      case 'reply':
        return [];
      case 'slotCliff':
        return (
          <li key="cliff">
            <span className="dot" style={{ background: DOT.slotCliff }} aria-hidden="true" />
            <div className="t">
              <b>{t('attention.slotCliff', { day: formatDayMedium(list[0]!.dayKey ?? '', i18n.language) })}</b>
              <span>{t('attention.slotCliffSub')}</span>
            </div>
          </li>
        );
      case 'completionReturned':
        return list.map((i) => (
          <li key={i.id} data-testid={`attn-returned-${i.id}`}>
            <span className="dot" style={{ background: DOT.completionReturned }} aria-hidden="true" />
            <div className="t">
              <b>
                {who(i)} · {t('attention.completionReturned')}
              </b>
              <span>{i.text}</span>
            </div>
            {finish.canFinish && i.appointment && role !== 'crew' ? (
              <Button
                size="sm"
                variant="secondary"
                aria-label={`${t('attention.correct')} · ${who(i)}`}
                data-testid={`attn-correct-${i.id}`}
                onClick={(e) =>
                  finish.openFinish(
                    { appointmentId: i.appointment!.appointmentId, unit: i.unit ?? '', tenantName: i.tenantName ?? '', serviceDateKey: i.dayKey ?? '' },
                    e.currentTarget,
                    { mode: 'resubmit', completionId: i.id, returnNotes: i.text ?? null },
                  )
                }
              >
                {t('attention.correct')}
              </Button>
            ) : null}
          </li>
        ));
      case 'completionReview':
        return (
          <li key="completion-review" data-testid="attn-completion-review">
            <span className="dot" style={{ background: DOT.completionReview }} aria-hidden="true" />
            <div className="t">
              <b>
                {t('attention.incidentReview')} · <span className="tnum">{list.length}</span>
              </b>
              <span>{list.slice(0, SHOW).map((i) => `${who(i)} · ${t(`finish.levels.${i.text}`, { defaultValue: i.text ?? '' })}`).join(', ')}</span>
            </div>
            <Link className="btn secondary sm" to="/admin/review">
              {t('attention.review')}
            </Link>
          </li>
        );
      case 'awaitingReview':
        return (
          <li key="review">
            <span className="dot" style={{ background: DOT.awaitingReview }} aria-hidden="true" />
            <div className="t">
              <b>{t('attention.awaitingReview', { count: list.length })}</b>
              <span>{list.slice(0, SHOW).map((i) => i.text).join(', ')}</span>
            </div>
            {role === 'admin' ? (
              <Link className="btn secondary sm" to="/admin/review">
                {t('attention.review')}
              </Link>
            ) : null}
          </li>
        );
      default:
        return null;
    }
  };

  const nothing = groups.length === 0 && !openLong.length && !closeoutCount;
  return (
    <Card aria-labelledby="h-attn" data-testid={tech === 'only' ? 'tech-attention' : 'attention'} className={top ? 'attn-top' : undefined}>
      <CardHeader>
        <CardTitle id="h-attn">{t('attention.title')}</CardTitle>
        {q.data ? (
          <span className="status attn has-icon">
            <span className="sdot" aria-hidden="true" />
            <span className="tnum">{total}</span>
            <span className="sr-only">{t('attention.itemsCount', { count: total })}</span>
          </span>
        ) : null}
      </CardHeader>
      <CardContent>
        {/* 18 Oct 2026: the day's close-outs come first — the one list of them (the Day computes the lines) */}
        {closeouts && closeoutCount ? (
          <>
            <h3 className="attn-group-h" id="h-pending">
              <Icon name="alert" /> {t('today.pendingCloseOut', { count: closeoutCount })}
              {closeouts.late ? ` · ${t('today.pendingLate', { count: closeouts.late })}` : ''}
            </h3>
            <ul className="attn-list" aria-labelledby="h-pending" data-testid="pending-close-out">
              {closeouts.rows}
            </ul>
          </>
        ) : null}
        {q.isLoading ? (
          <p className="help">{t('app.loading')}</p>
        ) : q.isError ? (
          <p className="help" role="alert">
            {t('attention.error')}
          </p>
        ) : nothing ? (
          top ? null : <p className="help">{t('attention.none')}</p>
        ) : groups.length === 0 && !openLong.length ? null : (
          <ul className="attn-list attn-guard">
            {/* 1.6.5 (D-1.6.5-5): a forgotten close-out surfaces by itself — a line, nothing automatic */}
            {openLong.map((o) => (
              <li key={`open-${o.appointmentId}`} data-testid={`attn-open-long-${o.appointmentId}`}>
                <span className="dot" style={{ background: DOT.pendingCloseout }} aria-hidden="true" />
                <div className="t">
                  <b>
                    <Icon name="clock" /> {t('press165.openTooLongFor', { unit: o.unit, time: openWords(o.minutes) })}
                  </b>
                </div>
              </li>
            ))}
            {groups.map((g) => line(g.kind, g.list))}
          </ul>
        )}
        {/* 18 Oct 2026 (D-1018-HK): the list ends with this line, so a person knows they heard it all */}
        {top && q.data && !q.isError && nothing ? (
          <p className="help" data-testid="attention-nothing">
            {t('attention.nothingElse')}
          </p>
        ) : null}
        <Updated at={q.dataUpdatedAt} />
      </CardContent>

    </Card>
  );
}
